import { useMemo } from 'react'

import { matchPath } from 'react-router'

import { COMMENT_REPLY_TYPE_SLUG } from '@bettermode/common/CMS'
import { hasScopesPermission } from '@tribeplatform/gql-client/lib'
import { PostTypeContext, type PostType } from '@tribeplatform/gql-client/types'
import { useRouter, useRouterPath } from '@tribeplatform/react-sdk'
import {
  useArchivePostType,
  useNetwork,
  useUnarchivePostType,
} from '@tribeplatform/react-sdk/hooks'
import { IconButton } from '@tribeplatform/react-ui-kit/Button'
import { confirm } from '@tribeplatform/react-ui-kit/Dialog'
import { Dropdown } from '@tribeplatform/react-ui-kit/Dropdown'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'
import { NavVertical } from '@tribeplatform/react-ui-kit/Sidebar'
import { toast } from '@tribeplatform/react-ui-kit/Toast'
import { Tooltip } from '@tribeplatform/react-ui-kit/Tooltip'

import { EntityImage } from '../../common/components/EntityImage.js'
import { T } from '../../i18n/components/T.js'
import { useI18n } from '../../i18n/index.js'
import { useNavigate } from '../../Link/hooks/useNavigate.js'

interface Props {
  postType: PostType
}
export const ModelItem = ({ postType }: Props) => {
  const { $t } = useI18n()
  const navigate = useNavigate()
  const { id, icon, name, archived, slug, context } = postType
  const { Link } = useRouter()
  const pathname = useRouterPath()
  const { mutateAsync: archive } = useArchivePostType()
  const { mutateAsync: unarchive } = useUnarchivePostType()
  const { data: network } = useNetwork()
  const [canUpdatePostType, canArchivePostType, canUnarchivePostType] =
    hasScopesPermission(network, [
      'updatePostType',
      'archivePostType',
      'unarchivePostType',
    ])

  const isActive = useMemo(() => {
    const isActive = matchPath(
      {
        path: `/manage/content/cms/${slug}`,
        end: false,
        caseSensitive: true,
      },
      pathname,
    )

    return !!isActive
  }, [slug, pathname])

  const archivePostType = async () => {
    try {
      const confirmed = await confirm({
        title: $t({
          defaultMessage: 'Are you sure you want to archive this model?',
          id: 'Admin.CMS.Archive.Confirm',
        }),
        description: $t({
          defaultMessage:
            'Archiving this model will prevent any future posts or replies from being created using this model. You can unarchive this model later.',
          id: 'Admin.CMS.Archive.Confirm.Description',
        }),
        proceedLabel: $t({
          defaultMessage: 'Archive',
          id: 'Generics.Archive',
        }),
        cancelLabel: $t({
          defaultMessage: 'Cancel',
          id: 'Generics.Cancel',
        }),
      })
      if (!confirmed) {
        return
      }

      await archive({ id })
      toast({
        title: $t({
          defaultMessage: 'CMS model successfully archived',
          id: 'Admin.CMS.Model.Archived',
        }),
        status: 'success',
      })
    } catch (error) {
      toast({
        title: $t({
          defaultMessage: 'Could not archive CMS model',
          id: 'Admin.CMS.Model.Archived.Error',
        }),
        status: 'error',
      })
    }
  }

  const unarchivePostType = async () => {
    try {
      await unarchive({ id })
      toast({
        title: $t({
          defaultMessage: 'CMS model successfully unarchived',
          id: 'Admin.CMS.Model.Unarchived',
        }),
        status: 'success',
      })
    } catch (error) {
      toast({
        title: $t({
          defaultMessage: 'Could not unarchive CMS model',
          id: 'Admin.CMS.Unarchived.Model.error',
        }),
        status: 'error',
      })
    }
  }

  return (
    <NavVertical.Item
      as={Link}
      href={
        context === PostTypeContext.post ? `/manage/content/cms/${slug}` : ''
      }
      active={isActive}
      leadingIcon={
        <div className="me-3 -ms-1">
          <EntityImage size="lg" entity={{ image: icon, name }} />
        </div>
      }
      className="ps-9"
    >
      <div className="flex-1 flex justify-between items-center min-w-0">
        <div className="flex-1 truncate">
          {context === PostTypeContext.post ? (
            <>{name}</>
          ) : (
            <Tooltip>
              <Tooltip.Trigger className="truncate inline">
                {name} <SvgIcon inline name="lock" />
              </Tooltip.Trigger>
              <Tooltip.Panel>
                <T
                  defaultMessage="Upcoming feature"
                  id="Admin.CMS.UpcomingFeature"
                />
              </Tooltip.Panel>
            </Tooltip>
          )}
        </div>
        <div
          onClick={e => {
            e.stopPropagation()
            e.preventDefault()
          }}
        >
          {!(
            context === PostTypeContext.reply &&
            slug === COMMENT_REPLY_TYPE_SLUG
          ) && (
            <Dropdown placement="bottom-end">
              <Dropdown.ButtonMinimal as="div">
                <IconButton
                  size="sm"
                  variant="tertiaryNeutral"
                  rounded
                  icon={<SvgIcon name="dots-horizontal" />}
                  className="hover:bg-transparent"
                />
              </Dropdown.ButtonMinimal>
              <Dropdown.Items>
                {!archived && canArchivePostType && (
                  <Dropdown.Item
                    leadingIcon={<SvgIcon name="archive" />}
                    onClick={archivePostType}
                  >
                    <T id="Generics.Archive" defaultMessage="Archive" />
                  </Dropdown.Item>
                )}
                {archived && canUnarchivePostType && (
                  <Dropdown.Item
                    leadingIcon={<SvgIcon name="reverse-left" />}
                    onClick={unarchivePostType}
                  >
                    <T id="Generics.Unarchive" defaultMessage="Unarchive" />
                  </Dropdown.Item>
                )}
                {context === PostTypeContext.post && canUpdatePostType && (
                  <Dropdown.Item
                    leadingIcon={<SvgIcon name="cog" />}
                    onClick={() => navigate(`/manage/content/cms/${slug}`)}
                  >
                    <T id="Generics.Settings" defaultMessage="Settings" />
                  </Dropdown.Item>
                )}
              </Dropdown.Items>
            </Dropdown>
          )}
        </div>
      </div>
    </NavVertical.Item>
  )
}
